import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Mazda MX-5 Miata (NA, ~1990) — the classic pop-up-headlight roadster at true
// scale (1 unit = 1 metre): length ~3.98 (SHORT), width 1.68, height 1.23
// (LOW), wheelbase 2.27, wheel radius ~0.30. Nose is +Z. The read is a tiny,
// cute, rounded, LOW open-top two-seat roadster with the TOP DOWN: a smooth
// rounded body, a short rounded bonnet, an OPEN cockpit (no roof) with two
// seats and headrest humps behind them, and a short rounded tail. The signature
// cues: the NA's ICONIC round POP-UP HEADLAMPS shown POPPED UP (round lamps
// standing on little raised pods on top of the front wings), a cute smiling
// oval lower grille/mouth, a low raked windscreen frame with no roof, twin
// roll-hoop hints, rounded flanks, round tail lamps and dual exhausts. There is
// deliberately NO flat deck at sill height behind the cabin — the cockpit is a
// shallow high recess with seats/headrests, so it reads as a roadster, never a
// pickup. Body panels carry the "paint" recolour slot (iconic Classic Red);
// `drive` spins the wheels and `hazards` blinks four indicators.

const PAINT = "#c62828"; // Mazda Classic Red — the "paint" slot default
const TRIM = "#141518"; // gloss-black trim (windscreen frame, roll hoops, buckets)
const WELL = "#141518"; // wheel wells
const GLASS = "#33485c"; // tinted windscreen glass
const CHROME = "#d6dade";
const SPOKE = "#c4c9cf"; // bright alloy spokes / rims
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#eef4f8"; // pop-up headlamp glass
const GRILLE = "#232529"; // dark oval intake
const CABIN = "#161619"; // near-black open interior (floor, side liners, dash box)
const SEAT = "#242429"; // seats / headrests (a touch lighter so they read in the well)
const INT = "#2b2b31"; // dash top / console (lighter still)
const RED = "#8e1520"; // tail-lamp lens (darker than the body so it reads on red)
const REDDARK = "#570d13"; // tail-lamp housing inner
const AMBER = "#e8a13a";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). SHORT, LOW, narrow — a tiny roadster.
const WHEEL_R = 0.30;
const TYRE_W = 0.18;
const WHEEL_X = 0.75; // half track — tyre outer face (0.84) proud of the body side (0.79) so wheels show from dead front
const AXLE_F = 1.05;
const AXLE_R = -1.22; // wheelbase 2.27
const BODY_X = 0.79; // main body half-width

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel box — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the rounded nose cap, tail cap, rear haunches and the headrest fairings. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** One classic MX-5 alloy: dark tyre, brake disc, recessed rim dish, five
 *  bright spokes slightly proud of the tyre's outer cap, a chrome centre cap.
 *  Every part shares `name` so the `drive` track spins them as one node about
 *  the axle. `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, TYRE_W, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.135, 0.135, 0.03, 16, BRAKE, [x - side * 0.05, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.185, 0.185, 0.06, 18, "#202225", [x + side * 0.05, y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.045, 0.32, 0.08, SPOKE, [x + side * 0.08, y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.03, 12, CHROME, [x + side * 0.09, y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The wheel arch: a dark well disc proud of the body side plus a thin
 *  body-colour brow (an upper half-torus over the tyre — MX-5 arches are
 *  smooth and body-coloured, not black-clad). The [0, pi/2, 0] yaw puts the
 *  0..pi arc exactly over the top half; the tyre's outer cap (0.82) stays
 *  proud of the well face (0.79). */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.36, 0.36, 0.04, 18, WELL, [side * 0.77, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.35, 0.04, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (BODY_X - 0.01), WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Fronts are amber chips on the bumper corners; rears are amber chips outboard
// of the tail lamps. (A classic car — four indicators only.)
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.55, 0.50, 1.86], size: [0.12, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.55, 0.50, 1.86], size: [0.12, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.70, 0.62, -1.955], size: [0.07, 0.10, 0.04] },
  { name: "indicator-rr", pos: [0.70, 0.62, -1.955], size: [0.07, 0.10, 0.04] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.045, 0.045, 1.4, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.46, 0.16, 3.2, TRIM, [0, 0.28, -0.05])); // underbody pan

  // -- Body masses (paint): a low rounded tub, short bonnet, short rear deck ---
  P.push(paint(1.58, 0.36, 3.5, [0, 0.46, -0.05])); // lower body tub, sill to floor plane (top 0.64)
  // Rounded flank shoulders soften the slab sides (thin body-colour fillets).
  for (const s of S) P.push(blob([0.05, 0.14, 1.5], [s * 0.79, 0.58, -0.05]));

  // Short bonnet: from the cowl (z 0.53) to the nose, tilted so the front dips.
  P.push(paint(1.28, 0.16, 1.25, [0, 0.72, 1.15], [0.05, 0, 0])); // bonnet (top 0.80 at cowl -> ~0.70 at nose)
  // Front wing crowns — raised fenders beside the bonnet that the pop-ups sit on.
  for (const s of S) P.push(paint(0.24, 0.16, 1.0, [s * 0.66, 0.78, 1.25], [0.05, 0, 0]));
  P.push(paint(1.28, 0.12, 0.16, [0, 0.80, 0.52])); // body-colour scuttle filling up to the windscreen base
  P.push(box(1.20, 0.02, 0.10, TRIM, [0, 0.865, 0.50])); // thin black cowl vent line

  // Rounded nose cap + smooth body-colour front bumper (front face ~2.00).
  P.push(paint(1.5, 0.30, 0.20, [0, 0.60, 1.86])); // nose block behind the grille
  P.push(blob([0.74, 0.13, 0.16], [0, 0.72, 1.84])); // rounded upper nose lip
  P.push(paint(1.5, 0.22, 0.16, [0, 0.44, 1.90])); // smooth lower bumper
  P.push(box(1.3, 0.18, 0.30, TRIM, [0, 0.34, 1.80])); // valance fill back to the arches (no see-through)

  // Short rounded rear deck + tail + smooth rear bumper.
  P.push(paint(1.5, 0.20, 1.10, [0, 0.72, -1.28])); // rear deck (top 0.82), short and belt-height
  P.push(blob([0.72, 0.12, 0.16], [0, 0.72, -1.82])); // rounded upper tail lip
  for (const s of S) P.push(blob([0.06, 0.17, 0.5], [s * 0.75, 0.58, -1.22])); // rounded rear haunches
  P.push(paint(1.52, 0.34, 0.12, [0, 0.60, -1.87])); // vertical tail panel (rear face ~-1.93)
  P.push(paint(1.5, 0.22, 0.16, [0, 0.44, -1.90])); // smooth rear bumper
  P.push(box(1.3, 0.18, 0.30, TRIM, [0, 0.34, -1.80])); // rear valance fill back to the arches

  // -- Pop-up headlamps: THE cue. Round lamps standing UP on little raised pods
  // on top of the front wings, facing forward, well above the bonnet line. ----
  for (const s of S) {
    P.push(box(0.28, 0.10, 0.32, PAINT, [s * 0.54, 0.92, 1.50], [-0.40, 0, 0], { slot: "paint" })); // body-colour pop-up lid, hinged up at the front (stands clearly proud of the wing crown)
    P.push(box(0.26, 0.15, 0.14, TRIM, [s * 0.54, 0.92, 1.54])); // black lamp bucket under the lens
    P.push({ geometry: new THREE.TorusGeometry(0.12, 0.024, 8, 18), color: CHROME, position: [s * 0.54, 1.00, 1.635] as Vec3 }); // chrome bezel ring, facing +Z
    P.push(cyl(0.105, 0.105, 0.05, 18, LAMP, [s * 0.54, 1.00, 1.62], [Math.PI / 2, 0, 0])); // round lamp lens, facing forward, proud of the bezel
  }

  // -- Smiley oval grille/mouth: a wide low oval intake on the smooth nose -----
  P.push({ // dark oval intake (a flattened disc facing +Z; no yaw, so the scale never shears it)
    geometry: new THREE.CylinderGeometry(0.18, 0.18, 0.05, 20),
    color: GRILLE,
    position: [0, 0.47, 1.985] as Vec3,
    rotation: [Math.PI / 2, 0, 0] as Vec3,
    scale: [1.85, 0.72, 1] as Vec3,
  });
  P.push({ // chrome lip around the oval mouth
    geometry: new THREE.TorusGeometry(0.18, 0.02, 8, 26),
    color: CHROME,
    position: [0, 0.47, 1.995] as Vec3,
    scale: [1.85, 0.76, 1] as Vec3,
  });
  P.push(cyl(0.04, 0.04, 0.02, 14, CHROME, [0, 0.67, 1.985], [Math.PI / 2, 0, 0])); // small chrome nose badge above the mouth

  // -- Low raked windscreen frame (NO roof behind it) -------------------------
  P.push(box(1.24, 0.30, 0.03, GLASS, [0, 0.965, 0.45], [-0.62, 0, 0])); // low raked screen glass (base meets the scuttle, top ~1.09)
  P.push(box(1.30, 0.05, 0.06, TRIM, [0, 1.10, 0.36])); // black header rail across the screen top
  for (const s of S) P.push(box(0.05, 0.34, 0.05, TRIM, [s * 0.60, 0.965, 0.45], [-0.62, 0, 0])); // A-pillar / frame sides
  for (const s of S) P.push(box(0.30, 0.012, 0.03, TRIM, [s * 0.22, 0.81, 0.47], [-0.62, 0, 0])); // wipers on the cowl

  // -- Open cockpit: a shallow HIGH recess with a dark interior, two seats,
  // headrests and a dash. Sits at belt height with seats/headrests standing up
  // — NOT a flat low bed. This is what makes it read as a roadster. ----------
  P.push(box(1.30, 0.05, 1.20, CABIN, [0, 0.65, -0.05])); // dark cockpit floor on the tub top
  for (const s of S) P.push(box(0.10, 0.16, 1.24, PAINT, [s * 0.72, 0.72, -0.05], undefined, { slot: "paint" })); // body-colour door-top rails (belt line)
  for (const s of S) P.push(box(0.03, 0.15, 1.18, CABIN, [s * 0.665, 0.72, -0.05])); // dark inner-door liners sealing the well sides
  P.push(box(1.22, 0.13, 0.14, INT, [0, 0.72, 0.42])); // dashboard behind the screen
  P.push(box(0.20, 0.14, 0.90, INT, [0, 0.70, -0.08])); // centre console / transmission tunnel
  for (const s of S) {
    P.push(box(0.42, 0.10, 0.44, SEAT, [s * 0.30, 0.71, -0.06])); // seat cushion
    P.push(box(0.42, 0.26, 0.10, SEAT, [s * 0.30, 0.84, -0.30], [-0.12, 0, 0])); // reclined seat back
    P.push(box(0.16, 0.12, 0.12, SEAT, [s * 0.30, 1.00, -0.34])); // headrest (below the screen top)
    P.push({ geometry: new THREE.TorusGeometry(0.11, 0.018, 6, 14, Math.PI), color: TRIM, position: [s * 0.30, 0.86, -0.50] as Vec3 }); // small twin roll hoop behind each headrest
    P.push(blob([0.22, 0.13, 0.24], [s * 0.30, 0.80, -0.60])); // body-colour headrest fairing flowing into the deck
  }
  P.push(box(0.03, 0.03, 0.24, TRIM, [-0.30, 0.80, 0.20], [0.5, 0, 0])); // steering column
  P.push({ geometry: new THREE.TorusGeometry(0.10, 0.016, 8, 16), color: TRIM, position: [-0.30, 0.83, 0.06] as Vec3, rotation: [1.2, 0, 0] as Vec3 }); // steering wheel

  // -- Rear fascia: round tail lamps in dark housings + plate + exhausts ------
  for (const s of S) {
    P.push(box(0.24, 0.16, 0.05, REDDARK, [s * 0.52, 0.62, -1.93])); // tail-lamp housing
    P.push(cyl(0.075, 0.075, 0.05, 16, RED, [s * 0.52, 0.62, -1.955], [Math.PI / 2, 0, 0])); // round red lens, proud of the housing
    P.push({ geometry: new THREE.TorusGeometry(0.08, 0.012, 6, 16), color: CHROME, position: [s * 0.52, 0.62, -1.965] as Vec3 }); // chrome lamp ring
  }
  P.push(box(0.34, 0.10, 0.02, PLATE, [0, 0.44, -1.965])); // rear plate
  P.push(cyl(0.05, 0.05, 0.02, 12, CHROME, [0, 0.72, -1.90], [Math.PI / 2, 0, 0])); // boot badge disc
  for (const s of S) P.push(cyl(0.035, 0.035, 0.10, 12, CHROME, [s * 0.24, 0.30, -1.98], [Math.PI / 2, 0, 0])); // dual exhaust tips

  // -- Front fascia extras ----------------------------------------------------
  P.push(box(0.34, 0.10, 0.02, PLATE, [0, 0.44, 1.985])); // front plate

  // -- Furniture: mirrors + door handles --------------------------------------
  for (const s of S) {
    P.push(box(0.06, 0.02, 0.02, TRIM, [s * 0.74, 0.80, 0.28])); // mirror arm
    P.push(paint(0.08, 0.08, 0.10, [s * 0.80, 0.82, 0.26])); // body-colour mirror cap
    P.push(box(0.06, 0.06, 0.012, CHROME, [s * 0.80, 0.82, 0.32])); // mirror glass
    P.push(box(0.02, 0.025, 0.11, CHROME, [s * 0.775, 0.66, -0.18])); // door handle
  }

  // -- Hazard indicators ------------------------------------------------------
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const mazdaMx5Na = defineModel({
  id: "mazda-mx5-na",
  name: "Mazda MX-5 (NA)",
  batchId: "cars",
  tags: ["car", "mazda", "mx5", "miata", "roadster", "convertible", "jdm", "classic"],
  summary: "A tiny rounded NA Mazda MX-5 Miata roadster, top down, pop-up lamps up, in Classic Red.",
  description:
    "A Mazda MX-5 Miata (NA, ~1990) at real 1 unit = 1 metre scale (about 3.98 " +
    "long, 1.68 wide, 1.23 tall, 2.27 wheelbase, wheel radius ~0.30). A tiny, " +
    "cute, rounded, LOW open-top two-seat roadster with the top down: a smooth " +
    "rounded body, a short rounded bonnet, an OPEN cockpit (no roof) with two " +
    "seats, headrest humps and twin roll hoops, and a short rounded tail. The " +
    "signature cues are all present: the iconic round POP-UP HEADLAMPS shown " +
    "popped up on little raised pods on top of the front wings, a cute smiling " +
    "oval lower grille/mouth, a low raked black windscreen frame, rounded " +
    "flanks with body-colour arches, round tail lamps, a boot badge and dual " +
    "exhausts, on classic MX-5 alloys. The cockpit is a shallow belt-height " +
    "recess with seats standing up (never a flat low bed), so it reads as a " +
    "roadster. Body panels carry the 'paint' recolour slot (Classic Red); " +
    "`drive` spins the wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.0,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
